import {
  ArrayLayoutProps,
  CellProps,
  ControlProps,
  EnumOption,
  LayoutProps,
  OwnPropsOfRenderer,
  StatePropsOfControlWithDetail,
  UISchemaElement,
} from '@jsonforms/core';
import Cookies from 'js-cookie';
import { useJsonForms } from '@jsonforms/react';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';

declare const window: any;

export const getFormOptions = (uischema: UISchemaElement) => {
  if (uischema.type === 'Form') {
    return uischema.options;
  }
  return {};
};
/**
 * Prepares the headers to be appended to the API from Form options
 * @param uischema
 * @returns
 */
export const getHeaders = (headerConfig: any): Headers => {
  const headers = new Headers();
  if (headerConfig && headerConfig.length > 0) {
    for (let index = 0; index < headerConfig.length; index++) {
      const apiHeader = headerConfig[index];
      const prepend = apiHeader?.bearer ? 'Bearer ' : '';
      if (typeof apiHeader.value !== 'object') {
        headers.append(apiHeader?.name, prepend + apiHeader?.value);
      } else {
        let storageContent;
        let value;
        const key = apiHeader.value?.key;
        const context = useJsonForms();
        if (key) {
          switch (apiHeader.value?.source) {
            case 'cookie':
              storageContent = Cookies.get(key);
              break;
            case 'localStorage':
              try {
                storageContent = localStorage.getItem(key);
              } catch (error) {
                console.error('Unable to read local storage');
              }
              break;
            case 'sessionStorage':
              try {
                storageContent = sessionStorage.getItem(key);
              } catch (error) {
                console.error('Unable to read session storage');
              }
              break;
            case 'custom':
              try {
                const customContent = get(context?.core?.data, key, '');
                if (customContent) {
                  headers.append(apiHeader?.name, prepend + customContent);
                }
              } catch (error) {
                console.error('Unable to read session storage');
              }
              break;
            case 'IMSBearer':
              try {
                const token = window?.adobeIMS?.getAccessToken()?.token;
                if (token) {
                  headers.append(
                    apiHeader?.name || 'Authorization',
                    'Bearer ' + token
                  );
                }
              } catch (error) {
                console.error('Unable to get Auth token');
              }
              break;
            default:
              break;
          }
        }
        if (storageContent) {
          // If value is JSON and a particular value within the JSON needs to be sent
          if (apiHeader.value?.path) {
            try {
              const parsedValue = JSON.parse(storageContent);

              if (parsedValue) {
                value = get(parsedValue, apiHeader.value.path);
              }
            } catch (error) {
              console.error('Value cannot be parsed to JSON', storageContent);
            }
          } else {
            value = storageContent;
          }
          headers.append(apiHeader?.name, prepend + value);
        }
      }
    }
  }
  return headers;
};
// getBaseUrl: replace the template literal with value
// example : path = "v1/api/${programType}/get-state?country=${country}", parameters = {"country":"UK","programType":"EC"}
// output: "v1/api/EC/get-state?country=UK"

export const getBaseUrl = (path: string, parameters: object) => {
  const params = new Map(Object.entries(parameters));
  const baseUrl = path.replace(/\$\{(.*?)\}/g, (...match) => {
    return params.has(match[1]) ? params.get(match[1]) : `${match[0]}`;
  });
  return baseUrl;
};

// formatDynamicEnumResponse: convert the response into {"label":"",value:""} format

// example1 : data = {"EC":{"data":[{"country":"India","countrycode":"IN"}]}}
// dataPath = "EC.data" , labelKey = "country" valueKey ="countrycode"
// output: [{"label":"India","value":"IN"}]

// example2 : data = {"EC":{"data":["UK","US","IN"]}}
// dataPath = "EC.data" , labelKey = "" valueKey =""
// output: [{"label":"UK","value":"UK"},{"label":"US","value":"US"},{"label":"IN","value":"IN"}]

export const formatDynamicEnumResponse = (
  data: any,
  dataPath: any,
  labelKey: any,
  valueKey: any
) => {
  let enumData = data;
  const enumOptions: any = [];
  if (dataPath) {
    enumData = get(data, dataPath, []);
  }
  if (enumData) {
    enumData.forEach((element: any) => {
      if (element != null && element.constructor.name === 'Object') {
        const label = get(element, labelKey);
        const value = get(element, valueKey);
        enumOptions.push({ label: label, value: value });
      } else {
        enumOptions.push({ label: element, value: element });
      }
    });
  }
  return enumOptions;
};

// getEndpointName : find the variable name from tamplate literel
// example endpointPath = "${environment.local.endpoint}","config"
// output = "environment.local.endpoint"

export const getEndpointName = (endpointPath: string, config: any) => {
  const matchEndpoint = endpointPath.match(/\$\{(.+?)\}/g);
  let pathKey;
  const currentEnvName = config?.environment?.currentEnvironment;

  if (matchEndpoint && matchEndpoint.length > 0) {
    let path = matchEndpoint[0];
    path = path.replace('${', '');
    pathKey = path.replace('}', '');
  } else {
    pathKey = endpointPath;
  }
  let pathList = pathKey.split('.');
  if (pathList.length === 2) {
    pathList = [pathList[0], currentEnvName, pathList[1]];
    pathKey = pathList.join('.');
  }

  return pathKey;
};

type JsonFormsPropTypes =
  | ControlProps
  | LayoutProps
  | CellProps
  | ArrayLayoutProps
  | StatePropsOfControlWithDetail
  | OwnPropsOfRenderer;

export const areEqual = (
  prevProps: JsonFormsPropTypes,
  nextProps: JsonFormsPropTypes
) => {
  const prev = omit(prevProps, [
    'schema',
    'uischema',
    'handleChange',
    'renderers',
    'cells',
    'uischemas',
  ]);
  const next = omit(nextProps, [
    'schema',
    'uischema',
    'handleChange',
    'renderers',
    'cells',
    'uischemas',
  ]);
  return (
    isEqual(prev, next) &&
    get(prevProps, 'renderers') === get(nextProps, 'renderers') &&
    get(prevProps, 'cells') === get(nextProps, 'cells') &&
    get(prevProps, 'uischemas') === get(nextProps, 'uischemas') &&
    get(prevProps, 'schema') === get(nextProps, 'schema') &&
    get(prevProps, 'uischema') === get(nextProps, 'uischema')
  );
};

/**
 * extracts account.name from ${account.name} and returns the value of 'account.name' from the passed context
 */
export const resolveContextValue = (context: any, key: string) => {
  const regex = /(\${([a-z._-]+)})/gim;
  let resolvedValue = key;

  const str = key;
  let m;

  while ((m = regex.exec(str)) !== null) {
    // This is necessary to avoid infinite loops with zero-width matches
    if (m.index === regex.lastIndex) {
      regex.lastIndex++;
    }

    const variable = m[1];
    const path = m[2];
    const value = get(context, path);
    // If value does not exists, replace with empty string
    resolvedValue = resolvedValue.replace(variable, value ?? '');
  }
  return resolvedValue;
};

/**
 * Converts from / separator  to . separator
 * Eg: /foo/bar/baz to foo.bar.baz
 */
export const convertToDotFormat = (instancePath: string) => {
  let updatedPath = '';
  if (instancePath?.startsWith('/')) {
    updatedPath = instancePath.slice(1).replace(/\//g, '.');
  }
  return updatedPath;
};

/**
 * Checks if there is an error in the errorArray for the given path,
 * if there is one, then checks if there a custom error for the same path.
 * If Yes, returns the custom error message
 * @param errorArray
 * @param customErrors
 * @returns
 */
export const getCustomErrorMessage = (
  errorArray: any,
  customErrors: any,
  path: string
) => {
  let customErrorMessage;
  errorArray?.forEach((element: any) => {
    let instancePath;

    // version 2.5
    if (element.dataPath) {
      instancePath = element.dataPath;
    } else {
      // version 3.X
      if (element.keyword === 'required') {
        if (element.instancePath) {
          instancePath =
            convertToDotFormat(element.instancePath) +
            '.' +
            element?.params?.missingProperty;
        } else {
          instancePath = element?.params?.missingProperty;
        }
      } else {
        instancePath = convertToDotFormat(element.instancePath) || path;
      }
    }

    if (instancePath === path && element.keyword != 'if') {
      const customError = customErrors[element.keyword] || element.message;
      if (customError) {
        customErrorMessage = customError;
      }
    }
  });
  return customErrorMessage;
};

export const convertToIdNameFormat = (items: EnumOption[]) => {
  const updatedItems = items.map((item: any) => {
    return {
      id: item.value,
      name: item.label,
      description: item.description,
    };
  });
  return updatedItems;
};

export const convertTitleConstToLabelValue = (items: any) => {
  const transformedData = items.map((item: any) => ({
    label: item.title,
    value: item.const,
  }));
  return transformedData;
};

export const convertArrayToLabelValue = (items: string[]) => {
  const transformedData = items.map((item) => ({
    label: item,
    value: item,
  }));
  return transformedData;
};

/**
 * Gets the last field name from path
 * Eg: 'state' from 'contacts.${index}.state'
 * @param fieldPattern
 * @returns
 */
export const getFieldNameFromPathPattern = (fieldPattern: string) => {
  const match = fieldPattern.match(/(?<=\${index}\.)\w+/);
  if (match) {
    return match[0];
  }
  return;
};

/**
 * Gets the index from the path pattern
 * Eg: '0' from 'contacts.0.state'
 * @param path
 * @param fieldPattern
 */
export const getIndexFromPathPattern = (path: string, fieldPattern: string) => {
  // Derive index pattern from the provided string pattern
  const indexPatternString = fieldPattern.replace('${index}', '(\\d+)');
  const indexPattern = new RegExp(indexPatternString);

  // Extract index from the original string
  const match = path.match(indexPattern);

  if (match && match[1]) {
    return parseInt(match[1]);
  }
  return;
};
